import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { StrideSectionReportData, Skill, StrideSectionReportStudentData } from '@mira-avant/mtlibs';
import { AngularModule } from '../../../../../../modules/angular.module';
import { ZorroModule } from '../../../../../../modules/zorro.module';
import { SkillService } from '../../../../../../services/skill.service';
import { Column, ReportingTable } from '../../../reporting-table/reporting-table';
import { Subscription } from 'rxjs';
import { BrowserService } from '../../../../../../services/browser.service';
import { HeaderColumn } from '../../../reporting-table/header-column';
import { RowColumn } from '../../../reporting-table/row-column';
import { CommonModule } from '@angular/common';
import { CognitoService } from '../../../../../../services/cognito.service';

const FULL_WIDTH_BREAKPOINT = 650;

@Component({
	selector: 'app-student-reports',
	standalone: true,
	imports: [CommonModule, AngularModule, ZorroModule, ReportingTable, RowColumn, HeaderColumn],
	templateUrl: './student-reports.component.html',
	styleUrl: './student-reports.component.scss',
})
export class StudentReportsComponent {
	@Output('selectStudent') public selectStudentEmitter = new EventEmitter<string>();

	@Input('report') set reportInput(value: StrideSectionReportData | undefined) {
		this.report.set(value);
		if (value) {
			this.loadReportData();
		}
	}

	report = signal<StrideSectionReportData | undefined>(undefined);
	skills = signal<string[]>([]);
	columns = signal<Column[]>([]);
	private wideColumns: Column[] = [];
	private narrowColumns: Column[] = [];
	MAX_LEVEL = signal(0);
	NUMBER_OF_LEVELS = signal(0);
	loading = signal(false);

	private sub?: Subscription;

	constructor(
		private skillService: SkillService,
		private browserService: BrowserService,
		private cognitoService: CognitoService
	) {
		this.skillService.getSkills$().then((skills: Skill[]) => {
			const codes = skills.map(skill => skill.code);
			codes.push('composite');
			this.skills.set(codes);
		});
	}

	private async loadReportData() {
		const currentReport = this.report();
		if (!currentReport) return;

		this.loading.set(true);

		try {
			const maxLevel = currentReport.classBreakdown?.length
				? Math.max(...currentReport.classBreakdown.map(item => item.rubricLevel.level))
				: 0;

			this.MAX_LEVEL.set(maxLevel);
			this.NUMBER_OF_LEVELS.set(currentReport.classBreakdown?.length || 0);

			// Load student display names
			const promises = currentReport.students.map(student =>
				this.cognitoService.getDisplayName({ user_uuid: student.studentUuid }).then(displayName => {
					student.studentName = displayName;
				})
			);

			await Promise.all(promises);

			this.updateTableDetails();

			// Resize listener
			this.sub?.unsubscribe();
			this.sub = this.browserService.onResize$.subscribe(width => {
				if (width >= FULL_WIDTH_BREAKPOINT) {
					this.columns.set(this.wideColumns);
				} else {
					this.columns.set(this.narrowColumns);
				}
			});
		} finally {
			this.loading.set(false);
		}
	}

	private updateTableDetails(): void {
		const currentReport = this.report();
		if (!currentReport) return;

		const studentNameColumn: Column = {
			key: 'studentName',
			name: 'Student Name',
			sortOrder: 'ascend',
			sortFn: (a: StrideSectionReportStudentData, b: StrideSectionReportStudentData) =>
				a.studentName.localeCompare(b.studentName),
			sortDirections: ['ascend', 'descend', null],
			filterMultiple: true,
			filterOptions: currentReport.students.map(row => ({
				text: row.studentName,
				value: row.studentName,
			})),
			filterFn: (list: string[], item: StrideSectionReportStudentData) =>
				list.some(name => item.studentName.indexOf(name) !== -1),
		};

		const skillColumns: Column[] = this.skills().map(skill => ({
			key: skill,
			name: skill,
			sortOrder: 'ascend',
			sortFn: (a: StrideSectionReportStudentData, b: StrideSectionReportStudentData) =>
				(a.skills[skill] || 0).toString().localeCompare((b.skills[skill] || 0).toString()),
			sortDirections: ['ascend', 'descend', null],
		}));

		this.wideColumns = [studentNameColumn, ...skillColumns];
		this.narrowColumns = [studentNameColumn];

		// Set initial columns based on current width
		const currentWidth = window.innerWidth;
		this.columns.set(currentWidth >= FULL_WIDTH_BREAKPOINT ? this.wideColumns : this.narrowColumns);
	}

	public selectStudent(studentUuid: string) {
		this.selectStudentEmitter.emit(studentUuid);
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
